import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Card from '../../UI/Card';
import ScheduleTimeline from './ScheduleTimeline';
import ScheduleFilter from './ScheduleFilter';
import TestPeekModal from '../TestingBoard/TestPeekModal';
import { useEPMContext } from '../../../contexts/EPMContext';
import { buildSchedule } from './scheduleData';

const SchedulePage = () => {
    const { tests, testLocations = [] } = useEPMContext();
    const [peekTestId, setPeekTestId] = useState(null);
    const closePeek = useCallback(() => setPeekTestId(null), []);

    const sortedLocations = useMemo(
        () => [...testLocations].sort((a, b) => Number(a.order || 0) - Number(b.order || 0)),
        [testLocations]
    );

    // Multi-select location fields: default to first configured location field
    const [selectedLocationKeys, setSelectedLocationKeys] = useState(() =>
        sortedLocations[0]?.key ? [sortedLocations[0].key] : []
    );

    const initializedRef = useRef(false);
    useEffect(() => {
        if (sortedLocations.length > 0 && !initializedRef.current) {
            setSelectedLocationKeys([sortedLocations[0].key]);
            initializedRef.current = true;
        }
    }, [sortedLocations]);

    const schedule = useMemo(
        () => buildSchedule(tests, {
            selectedLocationKeys,
            testLocations: sortedLocations,
        }),
        [tests, selectedLocationKeys, sortedLocations]
    );

    const { live, scheduled, proposed } = schedule.counts;

    return (
        <main className="stride-page-shell bg-gradient-to-b from-surface via-white to-white" aria-label="XCM schedule">
            <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-6">
                <div>
                    <p className="stride-eyebrow">Experiment programme</p>
                    <div className="flex items-baseline gap-4">
                        <h1 className="m-0 stride-heading text-2xl">Next 6 weeks</h1>
                        <span className="text-sm text-stride-muted">{live} live · {scheduled} scheduled{proposed ? ` · ${proposed} proposed` : ''}</span>
                    </div>
                </div>
                <ScheduleFilter
                    locations={sortedLocations}
                    selectedLocationKeys={selectedLocationKeys}
                    setSelectedLocationKeys={setSelectedLocationKeys}
                />
            </header>
            <Card variant="default" padding="none" className="overflow-hidden mb-6">
                <ScheduleTimeline schedule={schedule} onOpenTest={setPeekTestId} axisLabel="LOCATION" />
            </Card>
            {peekTestId && <TestPeekModal testId={peekTestId} onClose={closePeek} />}
        </main>
    );
};

export default SchedulePage;
